// Import React hooks for managing component state and optimizing calculations
import { useState, useMemo } from 'react';
// Import various icon components from Lucide React icon library
import { Pencil, Trash2, ChevronDown, ChevronUp, FolderOpen, CheckCircle, Clock, Folder, Target, Calendar, Check } from 'lucide-react';
// Import TypeScript type definitions for data structures
import { Project, Matter, Goal, Task } from '../../lib/db';
// Import the modal component for showing detailed project information
import { ProjectDetailModal } from './ProjectDetailModal';

// Define the shape of props this project card component expects
// TypeScript interface ensures correct data types are passed
interface ProjectCardProps {
  project: Project;        // The project data to display in this card
  matters: Matter[];       // Array of all matters (some may be linked to this project)
  goals: Goal[];          // Array of all goals/deliverables (some linked to this project)
  tasks?: Task[];         // Optional array of tasks (for calculating completion statistics)
  userId: string;         // Current user's ID (for permission checks)
  // Callback functions that parent components provide:
  onEdit: (project: Project) => void;           // Called when user clicks edit button
  onDelete: (project: Project) => void;         // Called when user confirms deletion
  onMarkComplete?: (project: Project) => void;  // Optional: Called when marking project complete
  onMarkIncomplete?: (project: Project) => void; // Optional: Called when marking project incomplete
  // Drag and drop functions for reordering projects:
  onDragStart: (e: React.DragEvent, project: Project) => void; // When user starts dragging
  onDragOver: (e: React.DragEvent) => void;     // When something is dragged over this card
  onDrop: (e: React.DragEvent, project: Project) => void;      // When something is dropped on this card
  onGoalsUpdate?: () => void;                   // Optional: Called when goals need refreshing
}

// ProjectCard displays a single project in a card format with expandable details
// Shows project information, associated matters, deliverables, and action buttons
export function ProjectCard({
  project,
  matters,
  goals,
  tasks,
  userId,
  onEdit,
  onDelete,
  onMarkComplete,
  onMarkIncomplete,
  onDragStart,
  onDragOver,
  onDrop,
  onGoalsUpdate,
}: ProjectCardProps) {
  // State to track whether the card is expanded to show additional details
  const [isExpanded, setIsExpanded] = useState(false);
  // State to control whether the detailed project modal is visible
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filter and organize data related to this specific project
  // Find all matters that belong to this project
  const associatedMatters = matters.filter(matter => matter.project_id === project.id);
  // Find goals/deliverables directly assigned to this project
  const directGoals = goals.filter(goal => goal.project_id === project.id);
  // Find goals that belong to matters within this project (indirect connection)
  const mattersGoals = goals.filter(goal => 
    goal.matter_id && associatedMatters.some(matter => matter.id === goal.matter_id)
  );
  // Combine direct and indirect goals
  const allProjectGoals = [...directGoals, ...mattersGoals];
  // Remove duplicates by creating a Map with goal ID as key, then getting unique values
  const uniqueGoals = Array.from(new Map(allProjectGoals.map(g => [g.id, g])).values());
  
  // useMemo optimizes performance by only recalculating statistics when tasks or goals change
  // This prevents expensive calculations on every render
  const statistics = useMemo(() => {
    // If no tasks data, return default statistics
    if (!tasks) return { totalTasks: 0, completedTasks: 0, totalTime: 0, allDeliverablesComplete: false };
    
    // Get IDs of all goals belonging to this project
    const goalIds = uniqueGoals.map(g => g.id);
    // Find all tasks that belong to this project's goals
    const projectTasks = tasks.filter(task => task.goal_id && goalIds.includes(task.goal_id));
    // Count how many tasks are marked as complete
    const completedTasks = projectTasks.filter(task => task.status === 'Complete').length;
    
    // Determine if all deliverables are complete
    // A deliverable is complete when ALL of its tasks are marked as complete
    const allDeliverablesComplete = uniqueGoals.length > 0 && uniqueGoals.every(goal => {
      const goalTasks = tasks.filter(task => task.goal_id === goal.id);
      // Goal is complete if it has tasks AND all tasks are complete
      return goalTasks.length > 0 && goalTasks.every(task => task.status === 'Complete');
    });
    
    return {
      totalTasks: projectTasks.length,    // Total number of tasks in project
      completedTasks,                     // Number of completed tasks
      totalTime: 0,                       // TODO: Calculate from time entries
      allDeliverablesComplete,            // Whether project is ready for completion
    };
  }, [tasks, uniqueGoals]); // Only recalculate when these dependencies change

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${project.title}"?`)) {
      onDelete(project);
    }
  };

  const getStatusColor = () => {
    switch (project.status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <div
        className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
        draggable
        onDragStart={(e) => onDragStart(e, project)}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, project)}
        onClick={(e) => {
          // Don't open modal if clicking on buttons or dragging
          if (!(e.target as HTMLElement).closest('button')) {
            setShowDetailModal(true);
          }
        }}
      >
        <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <FolderOpen className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">{project.title}</h3>
            {project.is_complete && (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
              {project.status}
            </span>
          </div>
          {project.description && (
            <p className="mt-1 text-sm text-gray-600">{project.description}</p>
          )}
          <div className="mt-2 flex items-center flex-wrap gap-2 text-sm text-gray-500">
            {project.start_date && (
              <span className="flex items-center text-green-600">
                <Calendar className="h-4 w-4 mr-1" />
                Start: {formatDate(project.start_date)}
              </span>
            )}
            {project.deadline && (
              <span className="flex items-center text-red-600">
                <Calendar className="h-4 w-4 mr-1" />
                Due: {formatDate(project.deadline)}
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center">
              <Folder className="h-4 w-4 mr-1" />
              {associatedMatters.length} matter{associatedMatters.length !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center">
              <Target className="h-4 w-4 mr-1" />
              {uniqueGoals.length} deliverable{uniqueGoals.length !== 1 ? 's' : ''}
            </span>
            {tasks && statistics.totalTasks > 0 && (
              <>
                <span className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {statistics.completedTasks}/{statistics.totalTasks} tasks
                </span>
                {statistics.totalTime > 0 && (
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {Math.round(statistics.totalTime / 60)}h
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => onEdit(project)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Edit project"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {statistics.allDeliverablesComplete && !project.is_complete && onMarkComplete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkComplete(project);
              }}
              className="p-1 text-gray-400 hover:text-green-600"
              title="Mark Project Complete"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          {project.is_complete && onMarkIncomplete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkIncomplete(project);
              }}
              className="p-1 text-green-600 hover:text-gray-600"
              title="Mark Project Incomplete"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleDelete}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Delete project"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isExpanded && (associatedMatters.length > 0 || directGoals.length > 0) && (
        <div className="mt-4 space-y-4 border-t pt-4">
          {associatedMatters.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700">Associated Matters:</h4>
              <ul className="mt-2 space-y-1">
                {associatedMatters.map(matter => (
                  <li key={matter.id} className="text-sm text-gray-600 pl-4">
                    • {matter.title}
                    {matter.status && matter.status !== 'Active' && (
                      <span className="ml-2 text-xs text-gray-500">({matter.status})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {directGoals.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700">Direct Deliverables:</h4>
              <ul className="mt-2 space-y-1">
                {directGoals.map(goal => (
                  <li key={goal.id} className="text-sm text-gray-600 pl-4">
                    • {goal.title}
                    {goal.category && (
                      <span className="ml-2 text-xs text-gray-500">({goal.category})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
    
    <ProjectDetailModal
      project={project}
      matters={matters}
      goals={goals}
      tasks={tasks}
      userId={userId}
      isOpen={showDetailModal}
      onClose={() => setShowDetailModal(false)}
      onEdit={onEdit}
      onGoalsUpdate={onGoalsUpdate}
    />
    </>
  );
}